import React, { useEffect } from 'react';

/**
 * Universal Payment Callback Component
 * This component ONLY shows a loader and auto-closes the popup
 * It parses JSON response from server to determine payment status
 * All actual success/failure UI is handled by the parent window
 */
const PaymentCallbackHandler = () => {
  useEffect(() => {
    const handlePaymentCallback = async () => {
      try {
        // Check if this is for a service or course payment
        const isServicePayment = window.location.pathname.includes('/student-selfcare/');
        
        // Extract course ID from URL for fallback (only for courses)
        const courseId = !isServicePayment ? extractCourseIdFromUrl(window.location.href) : null;
        const serviceType = isServicePayment ? 'selfcare' : null;
        
        console.log('[PaymentCallback] Payment type:', isServicePayment ? 'Service' : 'Course');
        console.log('[PaymentCallback] Current URL:', window.location.href);
        
        // Try to get JSON response from server
        let paymentData;
        
        // Check if there's a JSON response in the page content
        try {
          // Look for JSON data in page content or as a data attribute
          const bodyText = document.body.textContent || document.body.innerText;
          
          // Try to parse JSON from page content
          if (bodyText && bodyText.trim().startsWith('{')) {
            paymentData = JSON.parse(bodyText.trim());
          } else {
            // Fallback: Check for data in script tags or meta tags
            const scriptTags = document.querySelectorAll('script[type="application/json"]');
            if (scriptTags.length > 0) {
              paymentData = JSON.parse(scriptTags[0].textContent || '{}');
            }
          }
        } catch (parseError) {
          console.log('No JSON data found in page, falling back to URL parsing');
        }

        let paymentResult;

        if (paymentData) {
          // Parse JSON response from server
          console.log('Received payment data from server:', paymentData);
          
          const status = paymentData.status?.toLowerCase();
          const purchaseDetails = paymentData.purchase_details || {};
          const paymentResponse = paymentData.payment_response || {};
          
          // Determine redirect based on status
          let redirectType;
          if (status === 'success') {
            redirectType = 'success';
          } else if (status === 'cancelled') {
            redirectType = 'cancelled';
          } else {
            // All other statuses (failed, error, etc.) go to failure page
            redirectType = 'failed';
          }
          
          paymentResult = {
            type: 'PAYMENT_RESULT',
            success: status === 'success',
            status: status,
            redirectType: redirectType,
            purchaseId: purchaseDetails.purchase_id || undefined,
            transactionId: purchaseDetails.transaction_id || paymentResponse.gateway_transaction_id || undefined,
            courseId: courseId || (!isServicePayment ? extractCourseIdFromMerchantParams(paymentResponse) : undefined),
            serviceType: serviceType || undefined,
            amount: paymentResponse.amount || undefined,
            paymentMethod: paymentResponse.payment_method || undefined,
            orderId: paymentResponse.order_id || undefined,
            error: paymentData.message || paymentResponse.error_message || (status === 'success' ? undefined : 'Payment failed'),
            rawResponse: paymentData // Include full response for debugging
          };
        } else {
          // Fallback to URL-based parsing (legacy behavior)
          const urlParams = new URLSearchParams(window.location.search);
          const currentUrl = window.location.href;
          
          let redirectType;
          if (currentUrl.includes('/callback/success') || currentUrl.includes('/success')) {
            redirectType = 'success';
          } else if (currentUrl.includes('/cancelled')) {
            redirectType = 'cancelled';
          } else {
            redirectType = 'failed';
          }
          
          paymentResult = {
            type: 'PAYMENT_RESULT',
            success: redirectType === 'success',
            status: redirectType,
            redirectType: redirectType,
            purchaseId: urlParams.get('purchase_id') || undefined,
            transactionId: urlParams.get('transaction_id') || undefined,
            courseId: courseId || undefined,
            serviceType: serviceType || undefined,
            error: redirectType === 'success' ? undefined : (urlParams.get('error') || 'Payment failed')
          };
        }

        console.log('Sending payment result to parent:', paymentResult);

        // Send result to parent window
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(paymentResult, window.location.origin);
        }

        // Close popup after short delay to ensure message is sent
        setTimeout(() => {
          window.close();
        }, 1000); // 1 second delay for user to see "processing" message

      } catch (error) {
        console.error('Error in payment callback:', error);
        
        // Still notify parent of failure and close
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({
            type: 'PAYMENT_RESULT',
            success: false,
            status: 'failed',
            redirectType: 'failed',
            error: 'Payment processing error'
          }, window.location.origin);
        }
        
        setTimeout(() => {
          window.close();
        }, 1000);
      }
    };

    // Execute callback handling
    handlePaymentCallback();
  }, []);

  // ONLY show loader - never show success/failure content
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#f8f9fa',
      margin: 0,
      padding: '20px'
    }}>
      {/* Loading Spinner */}
      <div style={{
        width: '50px',
        height: '50px',
        border: '5px solid #e3e3e3',
        borderTop: '5px solid #007bff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
      }} />
      
      {/* Processing Message */}
      <h3 style={{
        color: '#343a40',
        marginBottom: '10px',
        textAlign: 'center'
      }}>
        Processing Payment...
      </h3>
      
      <p style={{
        color: '#6c757d',
        textAlign: 'center',
        fontSize: '14px',
        margin: 0
      }}>
        Please wait while we process your payment result.
      </p>

      {/* CSS Animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          body {
            margin: 0;
            padding: 0;
            overflow: hidden;
          }
        `}
      </style>
    </div>
  );
};

// Helper function to extract course ID from URL
const extractCourseIdFromUrl = (url: string): string | null => {
  const match = url.match(/\/buy-course\/([^\/]+)\//) || url.match(/course_id=([^&]+)/);
  return match ? match[1] : null;
};

// Helper function to extract course ID from merchant params in payment response
const extractCourseIdFromMerchantParams = (paymentResponse: any): string | null => {
  try {
    // Look for course ID in merchant_param1, merchant_param2, etc.
    const merchantParams = [
      paymentResponse.merchant_param1,
      paymentResponse.merchant_param2,
      paymentResponse.merchant_param3,
      paymentResponse.merchant_param4,
      paymentResponse.merchant_param5
    ];
    
    for (const param of merchantParams) {
      if (param && param.includes('course_id')) {
        // Extract UUID from patterns like "course_id:uuid" or "course_iduuid"
        const match = param.match(/course_id[:\s]*([a-f0-9-]{36})/i);
        if (match) {
          return match[1];
        }
      }
    }
    
    return null;
  } catch (error) {
    console.log('Error extracting course ID from merchant params:', error);
    return null;
  }
};

export default PaymentCallbackHandler;